import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Avatar,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { SwitchAccount } from "@mui/icons-material";
import { IImpersonatableUser } from "../../interfaces/impersonation";
import { TEntity } from "../../interfaces/userContext";
import {
  INTERNAL,
  MARKETPLACE,
  MERCHANT,
  ECOMMERCE,
} from "../../utils/constants";
import { formatDate } from "../../utils/common";
import ListPagination from "../tables/ListPagination";

interface UsersTableProps {
  users: IImpersonatableUser[];
  // eslint-disable-next-line no-unused-vars
  onImpersonate: (user: IImpersonatableUser) => void;
  impersonating: boolean;
  impersonatingUserId: number | null;
  // eslint-disable-next-line no-unused-vars
  setPagination: (params: { page: number; count: number }) => void;
  pagination: {
    page: number;
    count: number;
  };
}

const ENTITY_COLORS: Record<
  string,
  "primary" | "secondary" | "success" | "warning" | "error" | "info"
> = {
  [INTERNAL]: "error",
  [MARKETPLACE]: "primary",
  [MERCHANT]: "success",
  [ECOMMERCE]: "warning",
};

const ENTITY_LABELS: Record<string, string> = {
  [INTERNAL]: "Avenida",
  [MARKETPLACE]: "Tienda",
  [MERCHANT]: "Seller",
  [ECOMMERCE]: "Tienda",
};

const getEntityColor = (
  entityType: TEntity,
): "primary" | "secondary" | "success" | "warning" | "error" | "info" =>
  ENTITY_COLORS[entityType.toLowerCase()] || "info";

const getEntityLabel = (entityType: string): string =>
  ENTITY_LABELS[entityType.toLowerCase()] || entityType;

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onImpersonate,
  impersonating,
  impersonatingUserId,
  setPagination,
  pagination,
}) => (
  <TableContainer component={Paper} elevation={2}>
    <Table aria-label="tabla de usuarios">
      <TableHead>
        <TableRow className="bg-light">
          <TableCell className="text-bold">Usuario</TableCell>
          <TableCell className="text-bold">Email</TableCell>
          <TableCell className="text-bold">Entidad</TableCell>
          <TableCell className="text-bold">Tipo</TableCell>
          <TableCell className="text-bold">Roles</TableCell>
          <TableCell className="text-bold">Fecha de Creación</TableCell>
          <TableCell align="center" className="text-bold">
            Acción
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => {
          const isCurrentlyImpersonating =
            impersonating && impersonatingUserId === user.id;

          return (
            <TableRow key={user.id} className="menu-item-hover">
              <TableCell>
                <Box className="d-flex" sx={{ alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "primary.main",
                    }}
                    className="f-s-14"
                  >
                    {user.name?.charAt(0).toUpperCase() || ""}
                  </Avatar>
                  <Typography variant="body2" className="text-bold">
                    {user?.name || "Sin Nombre"}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2" className="text-muted">
                  {user.email}
                </Typography>
              </TableCell>

              <TableCell>
                <Tooltip title={`CUIT: ${user.entity.cuit}`} arrow>
                  <Typography variant="body2">{user.entity.name}</Typography>
                </Tooltip>
              </TableCell>

              <TableCell>
                <Chip
                  label={getEntityLabel(user.entity.type)}
                  color={getEntityColor(user.entity.type)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>

              <TableCell>
                <Box className="d-flex" sx={{ gap: 0.5, flexWrap: "wrap" }}>
                  {user.roles.map((role) => (
                    <Chip
                      key={role.id}
                      label={role.name}
                      size="small"
                      variant="filled"
                      color="success"
                      className="f-s-12"
                    />
                  ))}
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2" className="text-muted">
                  {formatDate(user.createdAt)}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={
                    isCurrentlyImpersonating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SwitchAccount sx={{ color: "inherit" }} />
                    )
                  }
                  onClick={() => onImpersonate(user)}
                  disabled={impersonating}
                >
                  {isCurrentlyImpersonating ? "Ingresando..." : "Impersonar"}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    <ListPagination
      total={pagination.count}
      page={pagination.page}
      onChangePage={(newPage) =>
        setPagination({ ...pagination, page: newPage })
      }
    />
  </TableContainer>
);
